/**
 * UpgradeModalContext — global, single-instance controller for the
 * "Upgrade to Portfolio Clarity" modal.
 *
 * Rationale: several <LockedFeature /> wrappers scattered across the app
 * must be able to open the SAME modal. Rather than each wrapper carrying
 * its own state, we lift it here so only one modal ever mounts.
 *
 * Pattern:
 *   <UpgradeModalProvider>
 *     ...app...
 *     <UpgradeToPortfolioModal />   ← rendered once, near root
 *   </UpgradeModalProvider>
 *
 *   const { openUpgradeModal } = useUpgradeModal();
 *   <LockedFeature onClick={openUpgradeModal}>...</LockedFeature>
 */
import { createContext, useContext, useState, useCallback, useMemo } from 'react';

const UpgradeModalContext = createContext(null);

export function UpgradeModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sourceFeature, setSourceFeature] = useState(null);

  const openUpgradeModal = useCallback((feature) => {
    setSourceFeature(feature || null);
    setIsOpen(true);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setIsOpen(false);
    setSourceFeature(null);
  }, []);

  const value = useMemo(
    () => ({ isOpen, sourceFeature, openUpgradeModal, closeUpgradeModal }),
    [isOpen, sourceFeature, openUpgradeModal, closeUpgradeModal],
  );

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal() {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    // Safe no-op fallback so <LockedFeature /> outside the provider still
    // renders without crashing (e.g. in isolated tests or storybook).
    return { isOpen: false, sourceFeature: null, openUpgradeModal: () => {}, closeUpgradeModal: () => {} };
  }
  return ctx;
}
